import { useState, useEffect } from 'react';
import { ref, onValue, get } from 'firebase/database';
import { database } from '../firebaseConfig';

/**
 * Lightweight hook that subscribes to the user's credit balance in realtime.
 *
 * For SUB-INSTANCES, it auto-resolves to the parent owner's pool — sub-
 * instances do not have their own credits. This mirrors the backend behavior
 * in `functions/deductCredits.js → resolveCreditOwnerUid()`. Result: every
 * sub-instance shows the same shared family balance, and consumption from
 * any sub draws from the same pool.
 *
 * Returns:
 *   { balance, monthlyBalance, bonusBalance, loading, sharedPool, creditOwnerUid, readFailed, lastUpdated }
 *     - balance        : number — monthly allowance plus extra credits that survive the reset
 *     - monthlyBalance : number — membership wallet, replaced on the account's cycle
 *     - bonusBalance   : number — one-time grants, kept across that reset
 *     - loading        : boolean
 *     - sharedPool     : boolean — true when reading from a parent
 *     - creditOwnerUid : the world whose pool is being subscribed to
 *     - lastUpdated    : ISO string | null — last write to the credits node
 *     - readFailed     : boolean — the subscription errored, so `balance` is a
 *                        0 placeholder and NOT a real balance. Callers that draw
 *                        conclusions from an empty balance (blocking a button,
 *                        announcing "out of credits") must check this first.
 */
const roundCredits = (n) => Math.round((Number(n) || 0) * 100) / 100;

export default function useCreditBalance(userId) {
  const [balance, setBalance] = useState(null);
  const [monthlyBalance, setMonthlyBalance] = useState(null);
  const [bonusBalance, setBonusBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creditOwnerUid, setCreditOwnerUid] = useState(null);
  const [sharedPool, setSharedPool] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [readFailed, setReadFailed] = useState(false);

  useEffect(() => {
    if (!userId) {
      setBalance(null);
      setMonthlyBalance(null);
      setBonusBalance(null);
      setLoading(false);
      setCreditOwnerUid(null);
      setSharedPool(false);
      setLastUpdated(null);
      setReadFailed(false);
      return undefined;
    }

    let cancelled = false;
    let unsubscribe = null;

    setLoading(true);

    // Resolve the actual credit-bearing world ONCE on mount / userId change.
    // The lookup is cheap (single shallow read) and parentOwnerUid never
    // changes during a session — so a one-shot resolve is safe and saves a
    // permanent listener.
    //
    // Read order matters: we ALWAYS hit `Users/{userId}/parentOwnerUid` first,
    // because for a sub-instance session both `auth.uid` and `userId` equal the
    // sub-world UID. Reading own data is unconditionally allowed by RTDB rules,
    // so this lookup never PERMISSION_DENIEDs. The downstream `World/{parent}/
    // credits` listener is gated by a dedicated rule that also allows
    // sub-world children of the parent (see firebase-rules.json → World/$userId/
    // credits.read). Without that rule, sub-worlds would silently see 0 credits.
    (async () => {
      let resolvedUid = userId;
      try {
        const parentSnap = await get(ref(database, `Users/${userId}/parentOwnerUid`));
        const parent = parentSnap.val();
        if (parent && typeof parent === 'string' && parent !== userId) {
          resolvedUid = parent;
        }
      } catch (err) {
        // Permission errors here are expected for non-owners of certain
        // worlds (e.g. team-member viewing the team owner's world without an
        // admin Users-read grant); fall back to the user's own path quietly.
        console.warn('[useCreditBalance] parent resolve failed, using self:', err?.code || err?.message);
      }

      if (cancelled) return;

      setCreditOwnerUid(resolvedUid);
      setSharedPool(resolvedUid !== userId);

      const creditsPath = `World/${resolvedUid}/credits`;
      const creditsRef = ref(database, creditsPath);
      unsubscribe = onValue(creditsRef, (snap) => {
        const val = snap.val() || {};
        const monthly = typeof val.spyCredits === 'number' ? val.spyCredits : 0;
        const bonus = typeof val.bonusCredits === 'number' ? val.bonusCredits : 0;
        setMonthlyBalance(monthly);
        setBonusBalance(bonus);
        setBalance(roundCredits(monthly + bonus));
        setLastUpdated(val.lastUpdated || null);
        setReadFailed(false);
        setLoading(false);
      }, (err) => {
        // If we ever hit PERMISSION_DENIED here it almost always means the
        // RTDB rule for World/$uid/credits is missing the sub-world or
        // teamMembership grant — log loud enough to triage but don't break
        // the UI: callers see balance=0 instead of a crash.
        console.warn(
          '[useCreditBalance] subscription error',
          { path: creditsPath, code: err?.code || err?.message, sharedPool: resolvedUid !== userId }
        );
        setBalance(0);
        setMonthlyBalance(0);
        setBonusBalance(0);
        setLastUpdated(null);
        setReadFailed(true);
        setLoading(false);
      });
    })();

    return () => {
      cancelled = true;
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, [userId]);

  return { balance, monthlyBalance, bonusBalance, loading, sharedPool, creditOwnerUid, lastUpdated, readFailed };
}

/**
 * Estimates total credit cost for a bulk operation.
 * Returns { totalCost, affordable, surplus }
 */
export function estimateBulkCost(contactCount, costPerItem, currentBalance) {
  const totalCost = contactCount * costPerItem;
  const affordable = currentBalance >= totalCost
    ? contactCount
    : Math.floor(currentBalance / costPerItem);
  return { totalCost, affordable, surplus: currentBalance - totalCost };
}
